import type { PriceSchema, PriceSchemaWithId } from "@repo/validators";
import type { z } from "zod";
import { useEffect } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Price } from "~/providers/general-provider";
import type { ManagerResult } from "~/types";
import { api } from "~/lib/api";
import { usePriceStore } from "~/stores/use-price-store";
import { usePermissionManager } from "./use-permission-manager";

export const usePriceManager = () => {
  const { mutate: createPrice, isPending: isCreatingPrice } =
    api.price.createPrice.useMutation();

  const { mutate: updatePrice, isPending: isUpdatingPrice } =
    api.price.updatePrice.useMutation();

  const { hasPermission } = usePermissionManager();

  const { setSelectedPrice, setPriceDialogOpen, setIsLoading, isLoading } =
    usePriceStore();

  const { t } = useTranslation("common");

  const createPriceHandler = async (
    price: z.infer<ReturnType<typeof PriceSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_price,
      label: t("Permissions.create_price_label"),
    });
    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof PriceSchema>>>
    >((resolve) => {
      if (!permission) {
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });
      }
      createPrice(price, {
        onSuccess() {
          toast.success(
            capitalize(t("create_item_success", { item: t("Price.price") })),
          );
          return resolve({
            data: true,
          });
        },
        onError() {
          return resolve({
            error: {
              path: "root",
              message: t("create_item_error", {
                item: t("Price.price"),
              }),
            },
          });
        },
      });
    });
  };

  const updatePriceHandler = async (
    price: z.infer<ReturnType<typeof PriceSchemaWithId>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_price,
      label: t("Permissions.update_price_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof PriceSchema>>>
    >((resolve) => {
      if (!permission) {
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });
      }
      updatePrice(price, {
        onSuccess() {
          toast.success(
            capitalize(t("update_item_success", { item: t("Price.price") })),
          );
          return resolve({
            data: true,
          });
        },
        onError() {
          return resolve({
            error: {
              path: "root",
              message: t("update_item_error", {
                item: t("Price.price"),
              }),
            },
          });
        },
      });
    });
  };

  const openPriceDialog = async (price?: Price) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_price,
      label: t("Permissions.create_price_label"),
    });

    if (!permission) return false;

    setSelectedPrice(price);
    setPriceDialogOpen(true);
  };

  useEffect(() => {
    setIsLoading(isCreatingPrice || isUpdatingPrice);
  }, [isCreatingPrice, isUpdatingPrice, setIsLoading]);

  return {
    createPrice: createPriceHandler,
    updatePrice: updatePriceHandler,
    openPriceDialog,
    isLoading,
  };
};
